// src/context/brandVoice/ui/VoiceTab.jsx — the "Personalità del brand" tab body (v190).
//
// The voice is NOT a field of the brand-guidelines document: its own route, its own etag PER
// LANGUAGE, ONE lock for the whole form. So the tab is:
//   intro → ONE FieldRow ("Personalità del brand": owner chip + Blocca / Sblocca, the SAME
//   fieldRules as every field, with the document read's `voice` block as its field) whose body
//   is the Italiano / English switch and, below it, the notes of that language and its form.
// ⛔ The lock reflects the ITALIAN row only — `/document/lock|unlock` with `'voice'` moves
// nothing else (backend limitation, roadmap § 5) — which is why the English view always says a
// hand-saved English is not updated by the AI any more.
// ⛔ `present !== true` (the workspace owns no voice file: the engine never creates one), a null
// `voice` or a failed document read → a field with `state: null`: no chip, no buttons.
// It owns ONE thing of its own, like DocFields: the Freeze / Unlock confirmation (both write
// immediately). ⛔ The page's `onFreeze` / `onUnlock` reject with OUR sentence — ConfirmDialog
// renders the rejection's `message` verbatim.
// ⛔ No completeness hint (never `/completeness`: it can start a billed refresh).
// ⭐ Not part of the base / Avanzate split (revised 2026-10-06): the Italiano / English switch and every axis are
// always shown, and the page offers no Avanzate toggle on this tab.
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import FieldRow from './FieldRow.jsx';
import VoiceForm from './VoiceForm.jsx';
import SegmentedTabs from '../../../components/ui/SegmentedTabs.jsx';
import ConfirmDialog from '../../../admin/components/ConfirmDialog.jsx';
import { voiceAxes } from '../voiceModel.js';
import { voiceErrorCopy } from '../copy.js';
import { COLORS, FONT_SANS, RADII, SEMANTIC } from '../../../constants/theme.js';

const TEXT = { fontSize: 13, color: COLORS.textSecondary, margin: '0 0 10px', lineHeight: '20px' };
const MUTED = { fontSize: 13, color: COLORS.textTertiary, margin: 0 };
const NOTE = (sem) => ({
  margin: '0 0 12px', padding: '10px 14px', borderRadius: RADII.control,
  background: sem.bg, color: sem.fg, fontSize: 13, lineHeight: '19px',
});
const RETRY = {
  marginLeft: 10, padding: '4px 10px', borderRadius: RADII.controlInner,
  border: `1px solid ${SEMANTIC.errore.fg}`, background: 'transparent', color: SEMANTIC.errore.fg,
  fontFamily: FONT_SANS, fontSize: 12, fontWeight: 600, cursor: 'pointer',
};
const NO_LOCK = { field_path: 'voice', state: null, engine_writable: false };

/**
 * @param {object} it / en      useBrandIdentity's voice / voiceEn parts
 * @param {'it'|'en'} lang      the language on screen (page state)
 * @param {object} values       { it, en } — what each form shows (draft or original)
 * @param {object} dirty        { it, en } — unsaved edits per language (the dots)
 * @param {Set} conflicts       languages whose stored voice moved under a draft (412 "Ricarica")
 * @param {object} problems     the save actions' problems (keys `voice.<lang>.<key>`)
 * @param {object|null} lock    the document read's `voice` block
 * @param {object} lockCtx      { dirty, busy, runLocked, unlockEffective } for fieldRules
 */
export default function VoiceTab({
  it, en, lang = 'it', onLang, values, dirty, conflicts, problems, lock, lockCtx, disabled,
  onChange, onFreeze, onUnlock, onRetry,
}) {
  const { t } = useTranslation('context');
  const [pending, setPending] = useState(null); // 'freeze' | 'unlock'
  const label = t('brandVoice.fields.voice');
  const lockField = lock && lock.present === true ? lock : NO_LOCK;
  const shownLang = lang;
  const isEn = shownLang === 'en';
  const langTabs = [
    { key: 'it', label: t('brandVoice.voice.lang.it'), dirty: !!dirty?.it },
    { key: 'en', label: t('brandVoice.voice.lang.en'), dirty: !!dirty?.en },
  ];

  const notes = () => {
    if (!isEn) {
      // (D) the platform's voice: the first Italian save takes the English away from English content.
      return it?.data?.source === 'global' && (
        <>
          <div role="status" style={NOTE(SEMANTIC.info)}>{t('brandVoice.defaultVoice')}</div>
          <p style={TEXT}>{t('brandVoice.voice.firstSaveEnglish')}</p>
        </>
      );
    }
    return (
      <>
        {/* (A) the English read was a 404: English content uses the Italian voice. */}
        {!en?.data && <div role="status" style={NOTE(SEMANTIC.info)}>{t('brandVoice.voice.noEnglish')}</div>}
        {/* (H) the platform's English voice can only be saved with, or after, the Italian. */}
        {en?.data?.source === 'global' && (
          <>
            <div role="status" style={NOTE(SEMANTIC.info)}>{t('brandVoice.defaultVoice')}</div>
            <p style={TEXT}>{t('brandVoice.voice.enWithItalian')}</p>
          </>
        )}
        {/* (E) always: the English row cannot be handed back to the AI from here. */}
        <p style={TEXT}>{t('brandVoice.voice.enManual')}</p>
      </>
    );
  };

  // The English read's own loading / error states show ONLY on the English view: the
  // ITALIAN read owns the tab (the page handles its states).
  const panel = () => {
    if (isEn && en?.loading) return <p style={MUTED}>{t('brandVoice.loading')}</p>;
    if (isEn && en?.error) {
      return (
        <div role="alert" style={NOTE(SEMANTIC.errore)}>
          <span>{voiceErrorCopy(t, en.error)}</span>
          {onRetry && <button type="button" onClick={onRetry} style={RETRY}>{t('brandVoice.errors.retry')}</button>}
        </div>
      );
    }
    return (
      <>
        {notes()}
        {conflicts?.has(shownLang) && <div style={NOTE(SEMANTIC.attenzione)}>{t('brandVoice.conflict')}</div>}
        <VoiceForm
          lang={shownLang}
          value={values?.[shownLang]}
          axes={voiceAxes(shownLang, it?.data, en?.data)}
          problems={problems}
          disabled={disabled}
          onChange={(next, key) => onChange?.(shownLang, next, key)}
        />
      </>
    );
  };

  return (
    <div>
      <FieldRow
        field={lockField}
        ctx={lockCtx || {}}
        label={label}
        problems={[]}
        conflict={false}
        onFreeze={() => setPending('freeze')}
        onUnlock={() => setPending('unlock')}
      >
        <p style={{ ...MUTED, marginBottom: 12 }}>{t('brandVoice.voice.bothLanguages')}</p>
        <SegmentedTabs
          tabs={langTabs} value={shownLang} onChange={onLang}
          label={t('brandVoice.voice.languages')} idPrefix="bv-lang"
          dirtyLabel={t('brandVoice.unsavedTab')}
        />
        {/* ⚠️ The ONE element both language tabs point at with aria-controls. */}
        <div role="tabpanel" id="bv-lang-panel" aria-labelledby={`bv-lang-${shownLang}`} style={{ paddingTop: 16 }}>
          {panel()}
        </div>
      </FieldRow>
      <ConfirmDialog
        open={!!pending}
        onClose={() => setPending(null)}
        onConfirm={() => (pending === 'freeze' ? onFreeze?.(lockField) : onUnlock?.(lockField))}
        title={pending ? t(`brandVoice.actions.${pending}Title`) : ''}
        message={pending ? t(`brandVoice.actions.${pending}Message`, { field: label }) : ''}
        // ⛔ No `confirmLabel` — same reason as DocFields: the default "Conferma" answers the title.
      />
    </div>
  );
}
